'use client';

import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import Link from './Link';
import { useClickOutside } from '@/lib/hooks';
import ChevronIcon from './ChevronIcon';
import LocaleSwitcher from '../blocks/LocaleSwitcher';

export interface DropdownItem {
    title: string;
    link: string;
}

interface DropdownProps {
    title: string;
    items: DropdownItem[];
    color?: 'white' | 'black';
    orientation?: 'left' | 'right';
    className?: string;
    menuClassName?: string;
    onOpen?: () => void;
    onClose?: () => void;
    onCloseMenu?: () => void;
}

export default function MoreDropdown({
    title,
    items,
    color = 'black',
    orientation = 'left',
    className,
    menuClassName,
    onOpen = () => {},
    onClose = () => {},
    onCloseMenu = () => {},
}: DropdownProps) {
    const [isOpen, setIsOpen] = useState(false);

    const isLeftOriented = orientation === 'left';
    const isBlack = color === 'black';

    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const handleClickOutside = () => setIsOpen(false);

    const dropdownRef = useClickOutside<HTMLDivElement>(handleClickOutside);

    useEffect(() => {
        isOpen ? onOpen() : onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <div
            ref={dropdownRef}
            className={cn(
                'relative w-fit h-fit',
                isLeftOriented ? 'text-left' : 'text-right',
                className,
            )}
        >
            <button
                className={cn(
                    'flex items-center w-full max-lg:text-[34px] lg:text-[26px] font-light hover:text-blue-200 lowercase transition-colors duration-300 font-helveticaNeue leading-none',
                    isBlack ? 'text-black' : 'text-tofu',
                    isOpen && 'text-blue-200',
                )}
                onClick={toggleDropdown}
            >
                {title}
                <span
                    className={cn(
                        'ml-2 max-lg:text-sm lg:text-p transition-transform duration-300',
                        isBlack ? 'text-black' : 'text-tofu',
                        !isOpen && 'rotate-180',
                    )}
                >
                    <ChevronIcon color={color} />
                </span>
            </button>
            {isOpen && (
                <ul
                    className={cn(
                        'lg:absolute z-10 top-full max-lg:gap-3 lg:gap-5 flex flex-col max-lg:mt-3 lg:mt-5 opacity-0 transition-opacity duration-300',
                        isBlack ? 'text-black' : 'text-tofu',
                        isLeftOriented ? 'left-0' : 'right-0',
                        isOpen && 'opacity-100',
                        menuClassName,
                    )}
                >
                    {items.map(({ title, link }, index) => (
                        <li key={index} className="block cursor-pointer">
                            <Link
                                className={cn(
                                    'max-lg:text-[34px] lg:text-[26px] lowercase font-light font-helveticaNeue leading-none text-nowrap',
                                    isBlack ? 'text-black' : 'text-tofu',
                                )}
                                href={link}
                                title={title}
                                onClick={() => {
                                    toggleDropdown();
                                    if (onCloseMenu) onCloseMenu();
                                }}
                            />
                        </li>
                    ))}
                    <li className="hidden lg:block cursor-pointer">
                        <LocaleSwitcher color={color} />
                    </li>
                </ul>
            )}
        </div>
    );
}
